import { OSId } from '@ps/platform-message-framework/common/OSId';
import { Cookie as CookieNames, FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { DismissIcon, StarsButton, Translation } from '@ps/web-shared-components';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';

import ShareApple from '@swf/core/assets/icons/share-apple.svg';
import useOneTrust from '@swf/core/hooks/useOneTrust';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { isIPadOS } from '@swf/core/utils';

import BannerNotification from '../../../components/BannerNotification/BannerNotification';

import {
  TXTWCL_Add_this_Web_App_to_your_homes_ELL,
  TXTWCL_Tap_N0_then_XXAdd_to_Homescree_ELL
} from './A2HS.messages.json';
import styles from './A2HS.scss';

const A2HS: React.FC = () => {
  const { alertClosed, oneTrustEnabled } = useOneTrust();
  const a2hsFeatureFlag = useFeature(FeatureFlag.Add2HomeScreen);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const a2hs = useSelector(select.notification.getCookies)[CookieNames.ADD_TO_HOME_SCREEN];
  const os = useSelector(select.device.getOS) === OSId.I_OS || isIPadOS;
  const seo = useSelector(select.cms.getSEO);
  const appleTouchIcons = useSelector(select.cms.getAppleTouchIcons);
  const {
    notification: { set }
  } = useDispatch<Dispatch>();
  const ipadIpadMiniUrl = appleTouchIcons?.ipad_ipad_mini?.url;
  const ipadProUrl = appleTouchIcons?.ipad_pro?.url;
  const iphoneUrl = appleTouchIcons?.iphone?.url;
  const iphoneXPlusUrl = appleTouchIcons?.iphone_x_plus?.url;
  const showBanner =
    (alertClosed || !oneTrustEnabled) && a2hs && isAuthenticated && a2hsFeatureFlag && os;

  return (
    <>
      <Helmet>
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black" />
        {seo?.meta.title && <meta name="apple-mobile-web-app-title" content={seo.meta.title} />}
        {!!iphoneXPlusUrl && <link rel="apple-touch-icon" href={iphoneXPlusUrl} />}
        {!!ipadIpadMiniUrl && (
          <link rel="apple-touch-icon" sizes="152x152" href={ipadIpadMiniUrl} />
        )}
        {!!ipadProUrl && <link rel="apple-touch-icon" sizes="167x167" href={ipadProUrl} />}
        {!!iphoneUrl && <link rel="apple-touch-icon" sizes="180x180" href={iphoneUrl} />}
      </Helmet>
      {showBanner && (
        <BannerNotification position="bottom" className={styles.banner}>
          <StarsButton
            role="button"
            className={styles.close}
            icon={<DismissIcon />}
            onClick={() => set({ name: CookieNames.ADD_TO_HOME_SCREEN, value: false })}
            size="small"
            variant="text"
          />
          {!!iphoneUrl && <img src={iphoneUrl} className={styles.image} alt="" />}
          <div className={styles.disclaimer}>
            <Translation tagName="p" {...TXTWCL_Add_this_Web_App_to_your_homes_ELL} />
            <Translation
              tagName="p"
              {...TXTWCL_Tap_N0_then_XXAdd_to_Homescree_ELL}
              values={{ 0: <ShareApple className={styles.shareApple} /> }}
            />
          </div>
        </BannerNotification>
      )}
    </>
  );
};

export default A2HS;
